import { useState } from 'react';
import {
  getReports,
  patchReport,
  patchUser,
  type AdminReport,
  type ReportStatus,
  type ReportUserRef,
} from '../api';
import { badgeClass, fmtDate, shortHash } from '../format';
import { useApi } from '../useApi';
import { Pager } from '../components/Pager';

const PAGE_SIZE = 50;

/** Name + @username from the enriched payload; falls back to the short raw id. */
function UserCell({ user, fallbackId }: { user?: ReportUserRef; fallbackId?: string }) {
  const id = user?.id ?? fallbackId;
  if (!user || (!user.displayName && !user.username)) {
    return (
      <span className="muted mono" title={id}>
        {shortHash(id)}
      </span>
    );
  }
  return (
    <span title={id}>
      {user.displayName ?? '—'}
      {user.username && <span className="muted"> @{user.username}</span>}
    </span>
  );
}

const labelOf = (user?: ReportUserRef, fallbackId?: string) =>
  user?.displayName ?? (user?.username ? `@${user.username}` : shortHash(user?.id ?? fallbackId));

export function Reports() {
  const [page, setPage] = useState(1);
  const { data, error, loading, reload } = useApi(() => getReports(page, PAGE_SIZE), [page]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const rows = data?.data ?? [];

  const moderate = async (id: string, status: ReportStatus) => {
    setBusyId(id);
    setActionError(null);
    try {
      await patchReport(id, status);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Update failed.');
    } finally {
      setBusyId(null);
    }
  };

  const banTarget = async (r: AdminReport) => {
    if (!r.targetUserId) return;
    if (!window.confirm(`Ban reported user ${labelOf(r.reported, r.targetUserId)}?`)) return;
    setBusyId(r.id);
    setActionError(null);
    try {
      await patchUser(r.targetUserId, { status: 'banned' });
      // banning resolves the report too — mark it actioned
      await patchReport(r.id, 'actioned');
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Ban failed.');
    } finally {
      setBusyId(null);
    }
  };

  const isClosed = (r: AdminReport) => r.status === 'actioned' || r.status === 'dismissed';

  return (
    <div className="admin-wrap">
      <div className="page-head">
        <h1>
          Reports<span className="dot">.</span>
        </h1>
      </div>
      {data && <p className="muted">{data.total} shown</p>}
      {actionError && <p className="error-line">{actionError}</p>}
      <div className="table-wrap">
        {error ? (
          <div className="empty">{error}</div>
        ) : loading && !data ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">No reports — the moderation queue is empty.</div>
        ) : (
          <table className="flm">
            <thead>
              <tr>
                <th>Reporter</th>
                <th>Target</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Detail</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <UserCell user={r.reporter} fallbackId={r.reporterUserId} />
                  </td>
                  <td>
                    <UserCell user={r.reported} fallbackId={r.targetUserId} />
                    {r.reported?.status && r.reported.status !== 'active' && (
                      <>
                        {' '}
                        <span className={badgeClass(r.reported.status)}>{r.reported.status}</span>
                      </>
                    )}
                  </td>
                  <td>{r.reason ?? '—'}</td>
                  <td>
                    <span className={badgeClass(r.status)}>{r.status ?? '—'}</span>
                  </td>
                  <td className="muted">{r.detail ?? '—'}</td>
                  <td className="muted">{fmtDate(r.createdAt)}</td>
                  <td>
                    <div className="row-actions">
                      {!isClosed(r) && (
                        <>
                          <button
                            className="btn"
                            disabled={busyId === r.id}
                            onClick={() => moderate(r.id, 'actioned')}
                          >
                            Resolve
                          </button>
                          <button
                            className="btn"
                            disabled={busyId === r.id}
                            onClick={() => moderate(r.id, 'dismissed')}
                          >
                            Dismiss
                          </button>
                        </>
                      )}
                      <button
                        className="btn btn-danger"
                        disabled={busyId === r.id || !r.targetUserId}
                        onClick={() => banTarget(r)}
                      >
                        Ban user
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && (
        <Pager page={data.page ?? page} pageSize={data.pageSize ?? PAGE_SIZE} total={data.total} loading={loading} onPage={setPage} />
      )}
    </div>
  );
}
